import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../i18n';
import {
  CONTACT_ROLES,
  contactFullName,
  type Contact,
  type ContactRole,
} from '../../types/api';
import { Button } from '../Button';
import { Input } from '../Input';
import { Modal } from '../Modal';
import { Select } from '../Select';
import { StatusPill } from '../StatusPill';
import { useToast } from '../Toast';
import styles from './ClientContacts.module.css';

// Issue #54 — contact list of a client with add/edit/delete and a primary
// marker. `readOnly` is used in the deal drawer (view-only roles, and the
// list simply hides itself if the user may not see this client).

interface ContactFormState {
  lastName: string;
  firstName: string;
  middleName: string;
  phone: string;
  email: string;
  role: ContactRole;
  positionNote: string;
}

const emptyForm: ContactFormState = {
  lastName: '',
  firstName: '',
  middleName: '',
  phone: '',
  email: '',
  role: 'OTHER',
  positionNote: '',
};

function toForm(c: Contact): ContactFormState {
  return {
    lastName: c.lastName,
    firstName: c.firstName,
    middleName: c.middleName ?? '',
    phone: c.phone,
    email: c.email ?? '',
    role: c.role,
    positionNote: c.positionNote ?? '',
  };
}

function payloadOf(f: ContactFormState) {
  return {
    lastName: f.lastName.trim(),
    firstName: f.firstName.trim(),
    phone: f.phone.trim(),
    role: f.role,
    middleName: f.middleName.trim() || null,
    email: f.email.trim() || null,
    positionNote: f.positionNote.trim() || null,
  };
}

export function ClientContacts({
  clientId,
  readOnly = false,
}: {
  clientId: string;
  readOnly?: boolean;
}) {
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const contactsQ = useQuery<Contact[]>({
    queryKey: ['clients', clientId, 'contacts'],
    queryFn: () => api.get<Contact[]>(`/clients/${clientId}/contacts`),
    retry: false,
  });

  const [modal, setModal] = useState<{ mode: 'add' } | { mode: 'edit'; contact: Contact } | null>(
    null,
  );
  const [form, setForm] = useState<ContactFormState>(emptyForm);
  const setField = <K extends keyof ContactFormState>(k: K, v: ContactFormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'contacts'] });

  const onError = (err: unknown) => {
    const msg = err instanceof ApiError ? err.message : undefined;
    toast.error(t('contacts.toastFail'), msg);
  };

  const saveMutation = useMutation({
    mutationFn: (data: { mode: 'add' | 'edit'; contactId?: string; payload: unknown }) =>
      data.mode === 'add'
        ? api.post<Contact>(`/clients/${clientId}/contacts`, data.payload)
        : api.patch<Contact>(`/clients/${clientId}/contacts/${data.contactId}`, data.payload),
    onSuccess: () => {
      invalidate();
      toast.success(modal?.mode === 'edit' ? t('contacts.toastUpdated') : t('contacts.toastCreated'));
      setModal(null);
    },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: (contactId: string) => api.delete(`/clients/${clientId}/contacts/${contactId}`),
    onSuccess: () => {
      invalidate();
      toast.success(t('contacts.toastDeleted'));
    },
    onError,
  });

  const primaryMutation = useMutation({
    mutationFn: (contactId: string) =>
      api.patch<Contact>(`/clients/${clientId}/contacts/${contactId}`, { isPrimary: true }),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError,
  });

  // Hidden for users who may not see this client (e.g. integrator + unbooked).
  if (contactsQ.error) return null;

  const contacts = contactsQ.data ?? [];
  const requiredOk = form.lastName.trim() && form.firstName.trim() && form.phone.trim();

  const openAdd = () => {
    setForm(emptyForm);
    setModal({ mode: 'add' });
  };
  const openEdit = (contact: Contact) => {
    setForm(toForm(contact));
    setModal({ mode: 'edit', contact });
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (saveMutation.isPending || !modal) return;
    saveMutation.mutate({
      mode: modal.mode,
      contactId: modal.mode === 'edit' ? modal.contact.id : undefined,
      payload: payloadOf(form),
    });
  };

  return (
    <section className={styles.section} data-testid="client-contacts">
      <div className={styles.headingRow}>
        <h3 className={styles.heading}>{t('contacts.title')}</h3>
        {!readOnly && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={openAdd}>
            {t('contacts.add')}
          </Button>
        )}
      </div>

      {contacts.length === 0 && !contactsQ.isLoading ? (
        <p className={styles.muted}>{t('contacts.empty')}</p>
      ) : (
        <ul className={styles.list}>
          {contacts.map((c) => (
            <li key={c.id} className={styles.item}>
              <div className={styles.itemMain}>
                <div className={styles.nameRow}>
                  <span className={styles.name}>{contactFullName(c)}</span>
                  {c.isPrimary && (
                    <StatusPill tone="success" subtle>
                      {t('contacts.primary')}
                    </StatusPill>
                  )}
                </div>
                <div className={styles.meta}>
                  {t(`contacts.roles.${c.role}`)}
                  {c.positionNote ? ` · ${c.positionNote}` : ''}
                  {` · ${c.phone}`}
                  {c.email ? ` · ${c.email}` : ''}
                </div>
              </div>
              {!readOnly && (
                <div className={styles.actions}>
                  {!c.isPrimary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={t('contacts.makePrimary')}
                      title={t('contacts.makePrimary')}
                      leftIcon={<Star size={14} />}
                      disabled={primaryMutation.isPending}
                      onClick={() => primaryMutation.mutate(c.id)}
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={t('contacts.edit')}
                    title={t('contacts.edit')}
                    leftIcon={<Pencil size={14} />}
                    onClick={() => openEdit(c)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={t('contacts.delete')}
                    title={t('contacts.delete')}
                    leftIcon={<Trash2 size={14} />}
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (window.confirm(t('contacts.deleteConfirm'))) deleteMutation.mutate(c.id);
                    }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={Boolean(modal)}
        onClose={() => !saveMutation.isPending && setModal(null)}
        title={modal?.mode === 'edit' ? t('contacts.modalEditTitle') : t('contacts.modalAddTitle')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setModal(null)}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="contact-form"
              loading={saveMutation.isPending}
              disabled={!requiredOk}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <form id="contact-form" onSubmit={submit} className={styles.form} noValidate>
          <Input
            label={t('contacts.fieldLastName')}
            value={form.lastName}
            onChange={(e) => setField('lastName', e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <Input
            label={t('contacts.fieldFirstName')}
            value={form.firstName}
            onChange={(e) => setField('firstName', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('contacts.fieldMiddleName')}
            value={form.middleName}
            onChange={(e) => setField('middleName', e.target.value)}
            fullWidth
          />
          <Select
            label={t('contacts.fieldRole')}
            value={form.role}
            onChange={(e) => setField('role', e.target.value as ContactRole)}
            options={CONTACT_ROLES.map((r) => ({ value: r, label: t(`contacts.roles.${r}`) }))}
            fullWidth
          />
          <Input
            label={t('contacts.fieldPhone')}
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('contacts.fieldEmail')}
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            fullWidth
          />
          <Input
            label={t('contacts.fieldPositionNote')}
            value={form.positionNote}
            onChange={(e) => setField('positionNote', e.target.value)}
            fullWidth
          />
        </form>
      </Modal>
    </section>
  );
}
